// apps/web/src/lib/compras/download.ts — Fase 13, Plano 19 (COMP-02, D-14, T-13-19C).
// Download autenticado de arquivo: GET com a sessão (cookie), blob e <a download> com o nome do Content-Disposition.
// O nome vem do servidor já sanitizado (13-16); mesmo assim é reduzido a um nome de arquivo seguro aqui.

/** Nome de arquivo usado quando o servidor não manda (ou manda um nome inutilizável). */
export const NOME_ARQUIVO_PADRAO = 'download';

/** Remove separadores de caminho, controles e aspas: o que sobra é só um nome de arquivo. */
function sanear(nome: string): string {
  const semControles = Array.from(nome)
    .filter((caractere) => caractere.charCodeAt(0) >= 0x20 && caractere.charCodeAt(0) !== 0x7f)
    .join('');
  const ultimoSegmento = semControles.split(/[\\/]/).pop() ?? '';
  return ultimoSegmento.replace(/["<>:|?*]/g, '').replace(/^\.+/, '').trim();
}

/**
 * Nome do arquivo no cabeçalho `Content-Disposition` (`filename="x.pdf"`, `filename=x.pdf` ou `filename*=UTF-8''x.pdf`).
 * null quando o cabeçalho não traz nome utilizável.
 */
export function nomeDoContentDisposition(cabecalho: string | null | undefined): string | null {
  if (!cabecalho) return null;
  const estendido = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(cabecalho);
  if (estendido?.[2]) {
    try {
      const nome = sanear(decodeURIComponent(estendido[2].trim()));
      if (nome) return nome;
    } catch {
      // Percent-encoding inválido: cai para o filename simples.
    }
  }
  const entreAspas = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(cabecalho);
  const simples = /filename\s*=\s*([^;]+)/i.exec(cabecalho);
  const bruto = entreAspas?.[1]?.replace(/\\(.)/g, '$1') ?? simples?.[1]?.trim();
  if (!bruto) return null;
  const nome = sanear(bruto);
  return nome === '' ? null : nome;
}

export class FalhaDownloadError extends Error {
  constructor(
    readonly status: number | null,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = 'FalhaDownloadError';
  }
}

/**
 * Baixa o arquivo de `url` com a sessão do usuário e dispara o download no navegador. Devolve o nome salvo.
 * Resposta que não é 2xx lança `FalhaDownloadError`; o object URL é sempre revogado.
 */
export async function baixarArquivo(url: string, padrao: string = NOME_ARQUIVO_PADRAO): Promise<string> {
  let resposta: Response;
  try {
    resposta = await fetch(url, { credentials: 'include' });
  } catch {
    throw new FalhaDownloadError(null, 'Falha de rede ao baixar o arquivo.');
  }
  if (!resposta.ok) throw new FalhaDownloadError(resposta.status, `HTTP ${resposta.status}`);

  const nome = nomeDoContentDisposition(resposta.headers.get('content-disposition')) ?? padrao;
  const blob = await resposta.blob();
  const objetoUrl = URL.createObjectURL(blob);
  try {
    const ancora = document.createElement('a');
    ancora.href = objetoUrl;
    ancora.download = nome;
    ancora.rel = 'noopener';
    ancora.style.display = 'none';
    document.body.appendChild(ancora);
    ancora.click();
    ancora.remove();
  } finally {
    // O clique já enfileirou o download; revogar no próximo ciclo evita cancelar a gravação em alguns navegadores.
    setTimeout(() => URL.revokeObjectURL(objetoUrl), 0);
  }
  return nome;
}
